/**
 * Trim and prepend https:// to scheme-less values. Must stay in agreement
 * with isValidUrl, which validates exactly the string this returns — that
 * pairing is what guarantees a validated value renders as a parseable href.
 */
export function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function isValidIpv4(hostname: string): boolean {
  const parts = hostname.split(".");
  if (parts.length !== 4) return false;

  return parts.every((part) => {
    if (!/^\d+$/.test(part)) return false;
    if (part.length > 1 && part.startsWith("0")) return false;
    const value = Number(part);
    return value >= 0 && value <= 255;
  });
}

function isValidIpv6(hostname: string): boolean {
  const normalized =
    hostname.startsWith("[") && hostname.endsWith("]")
      ? hostname.slice(1, -1)
      : hostname;
  if (!normalized.includes(":")) return false;
  return /^[0-9a-f:]+$/i.test(normalized);
}

function isValidDomain(hostname: string): boolean {
  if (hostname.length > 253) return false;

  const labels = hostname.split(".");
  if (labels.length < 2) return false;
  if (labels.some((label) => label.length === 0 || label.length > 63)) {
    return false;
  }
  if (
    labels.some(
      (label) =>
        !/^[a-z0-9-]+$/i.test(label) ||
        label.startsWith("-") ||
        label.endsWith("-"),
    )
  ) {
    return false;
  }

  const topLevelDomain = labels[labels.length - 1] ?? "";
  if (topLevelDomain.length < 2) return false;
  return /[a-z]/i.test(topLevelDomain);
}

export function isValidUrl(value: string | undefined): boolean {
  if (!value) return false;

  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) return false;

  const hasHttpScheme = /^https?:\/\//i.test(trimmed);
  const hasNonHttpScheme =
    /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed) && !hasHttpScheme;
  if (hasNonHttpScheme) return false;
  const authority =
    trimmed.replace(/^https?:\/\//i, "").split(/[/?#]/, 1)[0] ?? "";
  // A web address needs an explicit authority. URL would otherwise repair
  // leading slashes or backslashes into a host the applicant did not supply.
  if (!authority || authority.includes("\\")) return false;
  if (!hasHttpScheme && authority.includes("@")) return false;

  // Validate the exact string render sites pass to href, so a value this
  // function accepts can never produce an unparseable link.
  const candidate = normalizeUrl(trimmed);

  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return false;
    }

    const hostname = parsed.hostname.toLowerCase();
    if (isValidIpv4(hostname)) {
      // URL canonicalizes numbers, shortened IPs, hex and octal hosts into
      // dotted IPv4. Require the applicant to have supplied dotted decimal.
      const inputHostname = candidate.match(
        /^https?:\/\/(?:[^/?#]*@)?(\[[^\]]+\]|[^:/?#]+)/i,
      )?.[1];
      return inputHostname !== undefined && isValidIpv4(inputHostname);
    }
    return (
      hostname === "localhost" ||
      isValidIpv6(hostname) ||
      isValidDomain(hostname)
    );
  } catch {
    return false;
  }
}
